import { MouseCursor } from "@/sdk"
import { useAppDispatch, useAppSelector } from "@/store"
import { selectMousePos, setCustomCursor } from "@/stores/mouse_slice"
import { selectIsThreeDMouse } from "@/stores/remote_desk_state_slice"
import { MouseUseCase } from "@/usecases/mouse"
import React, { useCallback, useEffect, useRef, useState } from "react"

// 基于 Canvas 绘制的远程鼠标指针(全屏模式下使用)
export const CanvasRemoteCursor: React.FC = () => {
  // 鼠标样式图片cursor: data width height offsetX offsetY
  const [cursor, setCursor] = useState<MouseCursor | null>(null)

  // 鼠标在云桌面窗口中的坐标mousePos: {x,y}  redux中的
  const mousePos = useAppSelector(selectMousePos)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const dispatch = useAppDispatch()
  const isThreeDMouse = useAppSelector(selectIsThreeDMouse)

  useEffect(() => {
    // 发送光标数据
    MouseUseCase.onRemoteCursor((cursor) => {
      setCursor(cursor)
    })
  }, [])

  useEffect(() => {
    // if (mousePos === null || isThreeDMouse) {
    //   // 移出桌面时 ||  切换为3d鼠标时  显示系统鼠标
    //   if (canvasRef.current) {
    //     canvasRef.current.width = 0
    //     canvasRef.current.height = 0
    //   }
    //   return
    // }

    // 在鼠标重新进入云桌面窗口，或指针变化时，重新绘制，隐藏系统鼠标
    // 全屏模式下，直接使用绘制的鼠标，隐藏系统鼠标，不管移出/不移出云桌面
    dispatch(setCustomCursor("none")) // 隐藏系统鼠标

    // 3d鼠标模式下，不绘制鼠标
    if (isThreeDMouse) {
      if (canvasRef.current) {
        canvasRef.current.width = 0
        canvasRef.current.height = 0
      }
    } else {
      if (cursor !== null) {
        drawCursor(cursor) // 使用绘制的鼠标
      }
    }
  }, [mousePos, cursor, isThreeDMouse])

  // 绘制鼠标
  const drawCursor = useCallback((cursor: MouseCursor) => {
    const canvas = canvasRef.current
    if (canvas === null) {
      return
    }
    const ctx = canvas.getContext("2d")
    if (ctx === null) {
      return
    }

    if (canvas.width !== cursor.width || canvas.height !== cursor.height) {
      // 重置画布
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      canvas.width = cursor.width
      canvas.height = cursor.height
    }

    // 绘制鼠标指针
    ctx.putImageData(cursor.data, 0, 0)
  }, [])

  let top = 0
  let left = 0
  if (mousePos !== null && cursor !== null) {
    top = mousePos.y - cursor.offsetY
    left = mousePos.x - cursor.offsetX
  }

  return (
    <canvas
      id="canvas-mouse-cursor"
      ref={canvasRef}
      style={{
        position: "absolute",
        pointerEvents: "none",
        top,
        left,
        zIndex: 99999, // 确保鼠标始终在最上层
      }}
    />
  )
}

// 基于 CSS cursor 属性的远程鼠标指针(窗口模式下使用，也就是非全屏模式下)
export const CssRemoteCursor: React.FC = () => {
  const isThreeDMouse = useAppSelector(selectIsThreeDMouse)

  // 将 ImageData 转换为 data URL
  function imageDataToDataURL(imageData: ImageData): string {
    const canvas = document.createElement("canvas")
    canvas.width = imageData.width
    canvas.height = imageData.height
    const ctx = canvas.getContext("2d")
    if (!ctx) return ""
    ctx.putImageData(imageData, 0, 0)
    return canvas.toDataURL("image/png")
  }

  // 生成 CSS cursor 值
  function createCursorStyle(cursor: MouseCursor): string {
    const dataURL = imageDataToDataURL(cursor.data)

    if (!dataURL) return "default"
    // CSS cursor 语法: url(data:...) offsetX offsetY, fallback
    return `url(${dataURL}) ${cursor.offsetX} ${cursor.offsetY}, auto`
  }

  const dispatch = useAppDispatch()
  // 用于缓存上一次的光标数据 URL，避免重复创建相同的光标
  const lastCursorRef = useRef<string | null>(null)

  const handleCursorChange = useCallback(
    (cursor: MouseCursor) => {
      setCursor(cursor)
      const cursorStyle = createCursorStyle(cursor)

      // 只有当光标样式发生变化时才更新
      if (cursorStyle !== lastCursorRef.current) {
        lastCursorRef.current = cursorStyle
        dispatch(setCustomCursor(cursorStyle))
      }
    },
    [dispatch]
  )
  // 鼠标样式图片cursor: data width height offsetX offsetY
  const [cursor, setCursor] = useState<MouseCursor | null>(null)
  // 鼠标在云桌面窗口中的坐标mousePos: {x,y}  redux中的
  const mousePos = useAppSelector(selectMousePos)
  useEffect(() => {
    // 3d鼠标模式下，隐藏系统鼠标
    if (isThreeDMouse) {
      dispatch(setCustomCursor("none"))
    }

    if (mousePos === null) {
      // 移出云桌面时,显示系统鼠标
      dispatch(setCustomCursor(undefined))
    } else {
      // 进入云桌面时/指针变化时，重新绘制
      if (cursor !== null) {
        const cursorStyle = createCursorStyle(cursor)
        dispatch(setCustomCursor(cursorStyle))
      }
    }
  }, [mousePos, cursor, isThreeDMouse, dispatch])

  useEffect(() => {
    // 订阅光标变化事件
    MouseUseCase.onRemoteCursor(handleCursorChange)
  }, [handleCursorChange])

  // 不再渲染 canvas，光标样式通过 CSS cursor 属性在父组件应用
  return null
}
